import { cn } from '@/lib/utils'
import { Typo3Image } from '@/types'
import { BaseLink, Icon, Img, Richtext, Title } from '@/ui/components'

export interface CardInfoProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  title?: string
  icon?: string
  description?: string
  links?: {
    icon?: string
    title?: string
    href?: string
    target?: string
    location?: string
  }[]
  image?: Typo3Image
}

const CardInfo = ({
  title,
  links,
  icon,
  image,
  description,
  className,
  ...props
}: CardInfoProps) => (
  <div
    className={cn(
      'c-card-info pt-2xl px-lg mt-lg relative grid h-124 grid-rows-[auto_1fr] bg-neutral-200 text-black',
      className
    )}
    {...props}
  >
    {icon && (
      <div className="bg-accent-300 p-xs pl-lg absolute top-0 left-0 z-10 -translate-y-1/2 text-white">
        <Icon icon={icon} className="h-9 w-9" />
      </div>
    )}
    {image ? (
      <div className="absolute inset-0">
        <Img
          className="h-full w-full"
          data={image}
          sizes={`25rem`}
          objectCover
          fill
        />
      </div>
    ) : (
      <>
        <Title as="h3" className="h4 mb-xs line-clamp-3">
          {title}
        </Title>
        <div className="pb-2xl overflow-auto" data-lenis-prevent>
          {description && (
            <Richtext
              text={description}
              className="py-xs border-b border-neutral-600"
            />
          )}
          {links && links?.length > 0 && (
            <div>
              {links.map((link, index) => (
                <BaseLink
                  key={index?.toString()}
                  {...link}
                  className="gap-md py-xs grid grid-cols-[1fr_auto] border-b border-neutral-600"
                >
                  <div>
                    <span className="textstyle-sm-regular line-clamp-3 block text-neutral-600">
                      {link.title}
                    </span>
                    {link.location && (
                      <p className="textstyle-sm-regular line-clamp-1 text-neutral-400">
                        {link.location}
                      </p>
                    )}
                  </div>
                  {link.icon && (
                    <Icon icon={link.icon} className="mt-2xs h-3.5 w-3.5" />
                  )}
                </BaseLink>
              ))}
            </div>
          )}
        </div>
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-2 h-20 bg-linear-to-t from-neutral-200 to-transparent" />
      </>
    )}
  </div>
)

CardInfo.displayName = 'CardInfo'

export default CardInfo
